import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  FormControlLabel,
  InputLabel,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Collapse,
  InputAdornment,
} from '@mui/material';
import {
  Key,
  Add,
  Delete,
  Refresh,
  ContentCopy,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  Cancel,
  VpnKey,
  Business,
  AccessTime,
} from '@mui/icons-material';
import api from '../../services/api';

const PERMISSION_PRESETS = {
  PROFESSIONAL: [
    'catalog:read', 'catalog:write', 'catalog:create', 'catalog:delete', 'catalog:manage',
    'inventory:read', 'inventory:write',
    'pages:read', 'pages:write',
    'orders:read', 'orders:write',
    'website:read', 'website:write', 'website:update',
    'photos:upload', 'photos:read',
    'appointments:read', 'appointments:write',
    'reviews:read', 'reviews:write',
    'newsletter:read', 'newsletter:write',
  ],
  ENTERPRISE: [
    'catalog:read', 'catalog:write', 'catalog:create', 'catalog:delete', 'catalog:manage',
    'inventory:read', 'inventory:write',
    'pages:read', 'pages:write',
    'orders:read', 'orders:write',
    'website:read', 'website:write', 'website:update',
    'photos:upload', 'photos:read',
    'appointments:read', 'appointments:write',
    'reviews:read', 'reviews:write',
    'newsletter:read', 'newsletter:write',
    'customers:read', 'customers:write',
    'analytics:read', 'analytics:write',
    'admin:read', 'backup:create', 'audit:read',
  ],
  READ_ONLY: [
    'catalog:read', 'inventory:read', 'pages:read',
    'orders:read', 'website:read', 'photos:read',
    'appointments:read', 'reviews:read', 'newsletter:read',
  ],
};

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TierChip({ tier }) {
  const colors = {
    FREE: 'default',
    STANDARD: 'info',
    PROFESSIONAL: 'primary',
    ENTERPRISE: 'success',
  };
  return <Chip label={tier || 'FREE'} color={colors[tier] || 'default'} size="small" />;
}

function CompanyRow({ company, onKeyGenerated, onKeyRevoked, onKeyRotated, onExternalModeChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [keys, setKeys] = useState([]);
  const [keysLoading, setKeysLoading] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [externalMode, setExternalMode] = useState(!!company.externalWebsiteMode);
  const [externalModeSaving, setExternalModeSaving] = useState(false);

  const loadKeys = useCallback(async () => {
    setKeysLoading(true);
    try {
      const res = await api.get(`/api-access/keys/${company.id}`);
      setKeys(res.data.keys || []);
    } catch {
      setKeys([]);
    } finally {
      setKeysLoading(false);
    }
  }, [company.id]);

  useEffect(() => {
    if (expanded) loadKeys();
  }, [expanded, loadKeys]);

  useEffect(() => {
    setExternalMode(!!company.externalWebsiteMode);
  }, [company.externalWebsiteMode]);

  const handleExternalModeToggle = async (event) => {
    event.stopPropagation();
    const nextValue = event.target.checked;
    setExternalModeSaving(true);
    try {
      const res = await api.patch(`/api-access/companies/${company.id}/external-website-mode`, {
        enabled: nextValue,
      });
      setExternalMode(!!res.data.externalWebsiteMode);
      onExternalModeChanged(res.data);
    } catch (err) {
      console.error('External website mode update failed:', err);
      setExternalMode(!!company.externalWebsiteMode);
    } finally {
      setExternalModeSaving(false);
    }
  };

  const handleRevoke = async (keyId) => {
    try {
      await api.delete(`/api-access/keys/${keyId}`);
      await loadKeys();
      onKeyRevoked();
    } catch (err) {
      console.error('Revoke failed:', err);
    }
  };

  const handleRotate = async (keyId) => {
    try {
      const res = await api.post(`/api-access/keys/${keyId}/rotate`);
      await loadKeys();
      onKeyRotated(res.data);
    } catch (err) {
      console.error('Rotate failed:', err);
    }
  };

  return (
    <>
      <TableRow
        hover
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: 'pointer', '& > *': { borderBottom: 'unset' } }}
      >
        <TableCell>
          {expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium">{company.name}</Typography>
            {company.externalWebsiteMode && (
              <Chip label="External Site" color="info" size="small" variant="outlined" />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">{company.email || '—'}</Typography>
        </TableCell>
        <TableCell><TierChip tier={company.subscriptionTier} /></TableCell>
        <TableCell align="center">
          {parseInt(company.activeKeyCount) > 0 ? (
            <Chip
              icon={<CheckCircle fontSize="small" />}
              label={`${company.activeKeyCount} active`}
              color="success"
              size="small"
              variant="outlined"
            />
          ) : (
            <Chip
              icon={<Cancel fontSize="small" />}
              label="No keys"
              color="default"
              size="small"
              variant="outlined"
            />
          )}
        </TableCell>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTime fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {formatDate(company.lastApiUsed)}
            </Typography>
          </Box>
        </TableCell>
        <TableCell align="right" onClick={(e) => e.stopPropagation()}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Add />}
            onClick={() => setGenerateOpen(true)}
            disabled={parseInt(company.activeKeyCount) >= 3}
          >
            Generate Key
          </Button>
        </TableCell>
      </TableRow>

      {/* Expandable keys row */}
      <TableRow>
        <TableCell colSpan={6} sx={{ py: 0 }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Card variant="outlined" sx={{ mb: 2, bgcolor: 'action.hover' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={externalMode}
                        onChange={handleExternalModeToggle}
                        disabled={externalModeSaving}
                        onClick={(e) => e.stopPropagation()}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          External Website Mode
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Hides built-in website builder tools in the iOS app (page management, styling, configuration).
                          Automatically takes the built-in Novo website offline. Use when this business manages their website externally via the REST API.
                        </Typography>
                      </Box>
                    }
                    sx={{ alignItems: 'flex-start', ml: 0 }}
                  />
                </CardContent>
              </Card>
              {keysLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={20} />
                </Box>
              ) : keys.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No API keys for this company.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Prefix</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Permissions</TableCell>
                      <TableCell>Last Used</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {keys.map((key) => (
                      <TableRow key={key.id}>
                        <TableCell>
                          <Typography variant="body2">{key.name}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                          >
                            {key.keyPrefix}…
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {key.isActive ? (
                            <Chip label="Active" color="success" size="small" />
                          ) : (
                            <Chip label="Revoked" color="error" size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', maxWidth: 300 }}>
                            {(key.permissions || []).slice(0, 4).map((p) => (
                              <Chip key={p} label={p} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                            ))}
                            {(key.permissions || []).length > 4 && (
                              <Chip
                                label={`+${key.permissions.length - 4} more`}
                                size="small"
                                sx={{ fontSize: '0.65rem' }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(key.lastUsedAt)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">{formatDate(key.createdAt)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          {key.isActive && (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                              <Tooltip title="Rotate key">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleRotate(key.id)}
                                >
                                  <Refresh fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Revoke key">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleRevoke(key.id)}
                                >
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <GenerateKeyDialog
        open={generateOpen}
        company={company}
        onClose={() => setGenerateOpen(false)}
        onGenerated={(data) => {
          loadKeys();
          setGenerateOpen(false);
          onKeyGenerated(data);
        }}
      />
    </>
  );
}

function GenerateKeyDialog({ open, company, onClose, onGenerated }) {
  const [keyName, setKeyName] = useState('');
  const [preset, setPreset] = useState('');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setKeyName('');
      setPreset(company?.subscriptionTier === 'ENTERPRISE' ? 'ENTERPRISE' : 'PROFESSIONAL');
      setError('');
    }
  }, [open, company]);

  const handleGenerate = async () => {
    if (!keyName.trim()) {
      setError('Key name is required');
      return;
    }
    setGenerating(true);
    setError('');
    try {
      const res = await api.post('/api-access/keys', {
        companyId: company.id,
        name: keyName.trim(),
        permissions: PERMISSION_PRESETS[preset] || [],
      });
      onGenerated(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate key');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VpnKey color="primary" />
        Generate API Key — {company?.name}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label="Key name"
          placeholder="e.g. Developer Integration, Website Sync"
          value={keyName}
          onChange={(e) => setKeyName(e.target.value)}
          fullWidth
          margin="normal"
          autoFocus
        />
        <FormControl fullWidth margin="normal">
          <InputLabel>Permission preset</InputLabel>
          <Select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            label="Permission preset"
          >
            <MenuItem value="READ_ONLY">Read Only</MenuItem>
            <MenuItem value="PROFESSIONAL">Professional ({PERMISSION_PRESETS.PROFESSIONAL.length} permissions)</MenuItem>
            <MenuItem value="ENTERPRISE">Enterprise ({PERMISSION_PRESETS.ENTERPRISE.length} permissions)</MenuItem>
          </Select>
        </FormControl>
        <Alert severity="info" sx={{ mt: 2 }}>
          The full API key will be shown <strong>once</strong> after generation and cannot be retrieved again.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleGenerate}
          disabled={generating}
          startIcon={generating ? <CircularProgress size={16} color="inherit" /> : <Key />}
        >
          {generating ? 'Generating…' : 'Generate Key'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function NewKeyRevealDialog({ open, keyData, onClose }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(keyData?.apiKey || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!keyData) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CheckCircle color="success" />
        API Key Generated
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          <strong>Copy this key now.</strong> It will not be shown again.
        </Alert>
        <TextField
          fullWidth
          value={keyData.apiKey}
          inputProps={{ readOnly: true, style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip title={copied ? 'Copied!' : 'Copy to clipboard'}>
                  <IconButton onClick={handleCopy} edge="end">
                    <ContentCopy fontSize="small" color={copied ? 'success' : 'inherit'} />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Company:</strong> {keyData.companyName}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            &nbsp;·&nbsp;<strong>Prefix:</strong> <code>{keyData.keyPrefix}…</code>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            &nbsp;·&nbsp;<strong>Permissions:</strong> {(keyData.permissions || []).length}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={onClose}>
          Done — I've saved the key
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const DeveloperAPIAccessContent = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [newKeyData, setNewKeyData] = useState(null);
  const [revealOpen, setRevealOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api-access/companies');
      setCompanies(res.data.companies || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleKeyGenerated = (data) => {
    setNewKeyData(data);
    setRevealOpen(true);
    loadCompanies();
  };

  const handleKeyRevoked = () => {
    setSuccessMsg('API key revoked.');
    setTimeout(() => setSuccessMsg(''), 3000);
    loadCompanies();
  };

  const handleKeyRotated = (data) => {
    setNewKeyData(data);
    setRevealOpen(true);
    loadCompanies();
  };

  const handleExternalModeChanged = (data) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.id === data.companyId
          ? { ...c, externalWebsiteMode: data.externalWebsiteMode }
          : c
      )
    );
    setSuccessMsg(data.message || 'External Website Mode updated.');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const filtered = companies.filter((c) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || c.email?.toLowerCase().includes(search.toLowerCase())
  );

  const totalActive = companies.reduce((sum, c) => sum + parseInt(c.activeKeyCount || 0), 0);
  const companiesWithAccess = companies.filter((c) => parseInt(c.activeKeyCount || 0) > 0).length;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }}>{successMsg}</Alert>}

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Generate API keys to allow companies or third-party developers to access the Novo API.
          Keys are scoped to a single company and shown <strong>only once</strong> at creation.
          The website automatically validates keys — no restart required.
        </Typography>
      </Alert>

      {/* Summary cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 140px' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" fontWeight="bold" color="primary">{totalActive}</Typography>
            <Typography variant="body2" color="text.secondary">Active Keys</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 140px' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" fontWeight="bold" color="success.main">{companiesWithAccess}</Typography>
            <Typography variant="body2" color="text.secondary">Companies with Access</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 140px' }}>
          <CardContent sx={{ pb: '16px !important' }}>
            <Typography variant="h4" fontWeight="bold">{companies.length}</Typography>
            <Typography variant="body2" color="text.secondary">Total Companies</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Search + refresh */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search by company name or email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <Tooltip title="Refresh">
          <IconButton onClick={loadCompanies}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {/* Company table */}
      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell width={40} />
              <TableCell>Company</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell align="center">API Keys</TableCell>
              <TableCell>Last API Call</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No companies found.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((company) => (
                <CompanyRow
                  key={company.id}
                  company={company}
                  onKeyGenerated={handleKeyGenerated}
                  onKeyRevoked={handleKeyRevoked}
                  onKeyRotated={handleKeyRotated}
                  onExternalModeChanged={handleExternalModeChanged}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 3 }} />

      {/* Security notes */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="subtitle2" gutterBottom>Security Notes</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {[
              'Keys use the novo_ prefix and are 69 characters long (SHA-256 hashed at rest).',
              'Maximum 3 active keys per company.',
              'Rotating a key immediately deactivates the old one — no grace period.',
              'Revoking a key is instant; any in-flight requests using it will fail with 401.',
              'All key operations are audit-logged in the website database.',
            ].map((note) => (
              <Typography key={note} component="li" variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                {note}
              </Typography>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* New key reveal dialog */}
      <NewKeyRevealDialog
        open={revealOpen}
        keyData={newKeyData}
        onClose={() => { setRevealOpen(false); setNewKeyData(null); }}
      />
    </Box>
  );
};

export default DeveloperAPIAccessContent;
